<script setup lang="ts">
  import { defaultMessageDurationSeconds } from '@/utils'
  import { Button, message } from 'ant-design-vue'
  import {
    CopyOutlined,
    DownloadOutlined,
    EditOutlined,
    PlayCircleOutlined,
    SearchOutlined,
    UploadOutlined
  } from '@ant-design/icons-vue'
  import { nextTick, onMounted, onUnmounted, reactive, ref, toRef, watchEffect } from 'vue'
  import { useDark } from '@vueuse/core'
  import VueJsonPretty from 'vue-json-pretty'
  import 'vue-json-pretty/lib/styles.css'
  import JSONEditor from 'jsoneditor'
  import { debounceTime, Subject } from 'rxjs'
  import { AnalysisConfigSchema } from '../models/AnalysisConfigSchema'
  import { AnalysisConfig } from '../models/AnalysisConfig'
  import { AnalysisConfigService } from '../services/AnalysisConfigService'
  import { AnalysisConfigJsonParsingService } from '../services/AnalysisConfigJsonParsingService'
  import { InjectionService } from '@/shared/services/InjectionService'

  interface IAnalysisConfigJsonManagementProps {
    analysisConfigSchemas: Array<AnalysisConfigSchema>
    fileId: string
    analysisConfigs: Array<AnalysisConfig>
    visibilityChanged: boolean
  }

  interface IAnalysisConfigJsonManagementEmits {
    (e: 'configs-applied'): void
  }

  const props = defineProps<IAnalysisConfigJsonManagementProps>()
  const emit = defineEmits<IAnalysisConfigJsonManagementEmits>()

  const analysisConfigJsonParsingService: AnalysisConfigJsonParsingService =
    InjectionService.injectStrict(AnalysisConfigJsonParsingService.injectableKey)

  const fileId = toRef(props, 'fileId')
  const nonAppliedConfigs = reactive<Array<AnalysisConfig>>([...props.analysisConfigs])
  const activeAnalysisConfigs = reactive<Array<AnalysisConfig>>(props.analysisConfigs)
  const activeView = ref<'view' | 'edit'>('view')
  const editorHasChanges = ref<boolean>(false)

  const formattedConfigsJson = ref<any>()
  const dependencyChange$ = new Subject<void>()

  dependencyChange$.pipe(debounceTime(300)).subscribe(() => {
    formattedConfigsJson.value = AnalysisConfigService.getFormattedAnalysisConfigsJson(
      props.fileId,
      activeAnalysisConfigs
    )
  })

  watchEffect(() => {
    props.fileId
    activeAnalysisConfigs
    dependencyChange$.next()
  })

  const importedJson = ref<string | undefined>()
  const isDarkMode = useDark()
  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const toggleViewMode = () => {
    activeView.value = activeView.value === 'view' ? 'edit' : 'view'

    nextTick(() => {
      if (activeView.value === 'edit') {
        initializeJSONEditor()
      } else {
        destroyEditor()
      }
    })
  }

  const destroyEditor = () => {
    if (editor) {
      editor.destroy()
      editor = null
    }
  }

  const copyJsonToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(
        AnalysisConfigService.getFormattedAnalysisConfigsJsonString(
          props.fileId,
          activeAnalysisConfigs
        )
      )
      message.success('JSON copied to clipboard!', defaultMessageDurationSeconds)
    } catch (error) {
      message.error('Failed to copy to clipboard', defaultMessageDurationSeconds)
    }
  }

  // Ref for the hidden file input
  const fileInput = ref<HTMLInputElement | null>(null)

  const downloadJsonFile = () => {
    const jsonContent = AnalysisConfigService.getFormattedAnalysisConfigsJsonString(
      fileId.value,
      activeAnalysisConfigs
    )
    const blob = new Blob([jsonContent], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `analysis-configs-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    message.success('JSON file downloaded!', defaultMessageDurationSeconds)
  }

  const importJsonFile = () => {
    // Reset JSON Editor
    nonAppliedConfigs.splice(0, nonAppliedConfigs.length)
    setJSONEditorModel(fileId.value, nonAppliedConfigs)
    editorHasChanges.value = true
    fileInput.value?.click()
  }

  const handleFileSelected = (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]

    if (file) {
      // Optional: validate file type
      if (!file.name.endsWith('.json')) {
        message.error('Please select a valid .json file', defaultMessageDurationSeconds)
        return
      }

      const reader = new FileReader()
      reader.onload = () => {
        importedJson.value = reader.result as string
        const result = updateNonAppliedAnalysisConfigsFromJsonString(importedJson.value)
        if (result === true)
          message.success('JSON parsed successfully.', defaultMessageDurationSeconds)
      }
      reader.readAsText(file)
    }

    // Reset input value so the same file can be selected again later
    input.value = ''
  }

  const updateNonAppliedAnalysisConfigsFromJsonString = (json: string): boolean => {
    if (json == null) throw new Error('valid json required')
    const result = analysisConfigJsonParsingService.tryParseEntitiesFromJson(
      json,
      props.analysisConfigSchemas
    )
    if (!result.valid) {
      message.error(
        `Error parsing JSON: ${result.errors?.join(', ')}`,
        defaultMessageDurationSeconds
      )
      return false
    } else {
      // Passes validation should expect configs to exist
      nonAppliedConfigs.splice(
        0,
        nonAppliedConfigs.length,
        ...(result.parsedEntities as Array<AnalysisConfig>)
      )
      setJSONEditorModel(fileId.value, nonAppliedConfigs)
      return true
    }
  }

  const applyJsonConfigs = () => {
    try {
      const updatedPayloadString = editor?.getText()
      const result = updateNonAppliedAnalysisConfigsFromJsonString(updatedPayloadString)
      if (result === true) {
        message.success('JSON parsed and applied successfully.', defaultMessageDurationSeconds)
        activeAnalysisConfigs.splice(0, activeAnalysisConfigs.length, ...nonAppliedConfigs)
        editorHasChanges.value = false
      }
      emit('configs-applied')
    } catch (e) {
      message.error(`Error getting payload value from JSONEditor`, defaultMessageDurationSeconds)
    }
  }

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editorHasChanges.value = false
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          editorHasChanges.value = true
        }
      })
      setJSONEditorModel(fileId.value, activeAnalysisConfigs)
    }
  }

  const setJSONEditorModel = (fileId: string, analysisConfigs: Array<AnalysisConfig>) => {
    if (editor != null && editorContainer.value != null) {
      const json = AnalysisConfigService.getFormattedAnalysisConfigsJson(fileId, analysisConfigs)
      editor.set(json)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  onMounted(() => {
    initializeJSONEditor()
    formattedConfigsJson.value = AnalysisConfigService.getFormattedAnalysisConfigsJson(
      props.fileId,
      activeAnalysisConfigs
    )
  })

  onUnmounted(() => {
    destroyEditor()
  })
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Hidden file input -->
    <input type="file" ref="fileInput" accept=".json" class="hidden" @change="handleFileSelected" />

    <!-- Header Actions -->
    <div class="mb-4 flex justify-end gap-2">
      <a-tooltip placement="bottom" v-if="activeView === 'view'">
        <template #title>{{ 'Edit or Import JSON' }}</template>
        <Button class="flex items-center" type="primary" @click="toggleViewMode">
          <EditOutlined />
          {{ 'Edit JSON' }}
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom" v-else-if="activeView === 'edit'">
        <template #title>
          {{ editorHasChanges === true ? 'All changes will be lost.' : 'Select to view JSON.' }}
        </template>
        <Button
          :class="['flex items-center', { warning: editorHasChanges === true }]"
          @click="toggleViewMode"
        >
          <SearchOutlined />
          {{ 'View Only' }}
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom">
        <template #title>Copy Applied JSON to clipboard</template>
        <Button class="flex items-center" @click="copyJsonToClipboard">
          <CopyOutlined />
          Copy to Clipboard
        </Button>
      </a-tooltip>

      <a-tooltip placement="bottom">
        <template #title>Download Applied JSON</template>
        <Button class="flex items-center" @click="downloadJsonFile">
          <DownloadOutlined />
          Download JSON
        </Button>
      </a-tooltip>

      <Button class="flex items-center" :disabled="activeView !== 'edit'" @click="importJsonFile">
        <UploadOutlined />
        Import JSON
      </Button>
      <Button
        type="primary"
        class="flex items-center"
        :disabled="activeView === 'view' || editorHasChanges === false"
        @click="applyJsonConfigs"
      >
        <PlayCircleOutlined />
        Apply
      </Button>
    </div>

    <!-- JSON Content -->
    <div class="flex-1">
      <div class="relative z-[10] flex"></div>
      <div class="z-[1] mt-[10px]">
        <!-- View -->
        <div
          v-if="activeView === 'view'"
          class="overflow-auto rounded-sm border bg-card p-1"
          style="max-height: calc(100vh - 300px)"
        >
          <div :class="['json-pretty-wrapper', 'viewer-container', { 'dark-mode': isDarkMode }]">
            <vue-json-pretty :data="formattedConfigsJson as any" />
          </div>
        </div>
        <!-- Edit -->
        <div class="editor-container" v-else-if="activeView === 'edit'" ref="editorContainer"></div>
      </div>
    </div>
  </div>
</template>

<style>
  .editor-container {
    height: calc(100vh - 165px);
  }
  .viewer-container {
    min-height: calc(100vh - 165px);
    height: 100%;
  }
</style>
